import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getRealtimeDatabase } from "src/utils/firebase";
import BackgroundImg from "../../assets/images/Background_Img.png";
import { Button, useTheme } from "@mui/material";
import { sendEmail } from "src/utils/service.js";

const DemoComplete = () => {
  const { uid, type } = useParams();
  const db = getRealtimeDatabase();
  const theme = useTheme();

  const [userData, setUserData] = useState(null);
  const [emailStatus, setEmailStatus] = useState("idle"); // idle | sending | sent | error

  useEffect(() => {
    if (!db || !uid || !type) return;

    const userRef = db.ref(`demo_users/${type}/${uid}`);

    // ✅ Update demo completion state
    userRef.update({
      done: true,
      type: type || "default",
      completedAt: new Date().toISOString(),
    });

    // ✅ Fetch user info
    userRef.once("value").then((snapshot) => {
      const data = snapshot.val();
      setUserData(data);

      // ✅ Send email automatically if not already sent and email present
      if (data && data.email && !data.emailSent) {
        setEmailStatus("sending");
        console.log("📧 Sending email to:", data);
        sendInvestorEmail(data)
          .then(() => {
            userRef.update({
              emailSent: true,
              emailSentAt: new Date().toISOString(),
            });
            setEmailStatus("sent");
          })
          .catch(() => {
            setEmailStatus("error");
          });
      }
    });
  }, [uid, type, db]);

  // ✅ Lookup table for messages
  const messages = {
    selfie:
      "Awesome! You have completed the Selfie.",
    basic: "Thank you for viewing our Basic Demo!",
    advanced: "Thank you for completing the Advanced Demo!",
    just_info: "Thanks for providing your info!",
    tractor: "Great job! You’ve completed the Tractor demo.",
    safety: "Thanks for checking out the Safety demo.",
    stopped_at_info:
      "Sorry, the admin has ended your session. You can scan the QR code again to restart.",
    default:
      "Awesome! You have completed the demo — we hope you enjoyed the experience.",
  };
  console.log("ℹ️ Demo type:", type, "message:", messages[type]);
  const message = messages[type] || messages.default;

  async function sendInvestorEmail(userData) {
    try {
      
      const selfieSection = userData.photoUrl
        ? `
          <!-- Selfie Section -->
          <tr>
            <td style="padding:30px; text-align:center; background:#ffffff; color:#333333;">
              <h3 style="margin:0 0 15px 0; color:#111111;">📸 Your Selfie</h3>
              <p>Here’s the selfie you submitted during the demo:</p>
              <a href="${userData.photoUrl}" target="_blank">
                <img src="${userData.photoUrl}" alt="Selfie" 
                     style="max-width:300px; border-radius:8px; border:1px solid #ccc;">
              </a>
            </td>
          </tr>
        `
        : "";

      await sendEmail({
        to: userData.email,
        subject:
          "Thank you for your interest in iTARRA – Building the Future Together",
        bodyHtml: `
          <!DOCTYPE html>
          <html lang="en" style="margin:0; padding:0; font-family:Arial, sans-serif;">
          <head><meta charset="UTF-8"><title>iTARRA | The Future is Different</title></head>
          <body style="margin:0; padding:0; background-color:#f4f4f4;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f4; padding:20px;">
              <tr>
                <td align="center">
                  <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff; border-radius:8px; overflow:hidden;">
                    <!-- Header / Logo -->
                    <tr>
                      <td style="padding:20px; text-align:center; background:#111111;">
                        <img src="https://static.wixstatic.com/media/0faa34_ec9b6bcccf0c4f9eadc98e2ccfb42f41~mv2.png/v1/fill/w_217,h_53,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/iTarra-LOGO-2022-Outline%20(1).png" alt="iTARRA Logo" width="180" style="display:block; margin:auto;">
                      </td>
                    </tr>

                    <!-- Hero Banner -->
                    <tr>
                      <td style="padding:30px; text-align:center; background:#30BDFF; color:#ffffff;">
                        <h1 style="margin:0; font-size:26px;">Building the Future Together</h1>
                        <p style="margin:10px 0 0; font-size:16px;">Innovation for Farmers, Builders & Beyond</p>
                      </td>
                    </tr>

                    <!-- Body Content -->
                    <tr>
                      <td style="padding:30px; color:#333333; font-size:15px; line-height:1.6;">
                        <p>Dear ${userData.name || "Friend"},</p>
                        <p>
                          Thank you for your interest in the iTARRA platform. As CEO, I want to personally acknowledge 
                          how important it is to keep innovation alive for farmers, people working in construction, 
                          and those shaping the future of industry.
                        </p>
                        <p>
                          At iTARRA, we believe the future is built on strong partnerships and forward thinking. That’s 
                          why we’re working closely with <b>ATU Sligo</b> on cutting-edge robotics and educational 
                          programs, ensuring the next generation can learn, apply, and expand on the technologies 
                          that power our products.
                        </p>
                        <p>
                          We’re also partnering with <b>TUS Athlone</b> on <b>5G standalone</b> technology, in 
                          collaboration with <b>Three Ireland</b>, to deliver fast, safe, and reliable connections 
                          to machines and infrastructure.
                        </p>
                        <p>
                          And we are proud to partner with <b>Google Cloud</b>, giving us world-class tools to scale 
                          our data, AI, and edge robotics solutions.
                        </p>
                        <p>
                          If you are exploring opportunities to <b>invest in the future of agri, construction, 
                          and defence innovation</b>, I’d love to hear from you.
                        </p>
                        <p>The future is important — and together, we can build it.</p>
                        <p>Warm regards,</p>
                        <p><b>David Doran</b><br>CEO, iTARRA</p>
                      </td>
                    </tr>

                    ${selfieSection}

                    <!-- Learn More Section -->
                    <tr>
                    <td style="padding:30px; background:#f9f9f9; color:#333333; font-size:15px; line-height:1.6;">
                        <h3 style="margin:0 0 15px 0; color:#111111;">📚 Learn More About Our Platform</h3>
                        <ul style="padding-left:20px; margin:0;">
                        <li>
                            <a href="https://firebasestorage.googleapis.com/v0/b/myitarra-95fb4.appspot.com/o/media%2FItarra%20Platform.pdf?alt=media&token=e1648fb7-32a2-4747-bd4f-4b396442895c" target="_blank">
                            iTARRA Platform Overview
                            </a>
                        </li>
                        <li>
                            <a href="https://firebasestorage.googleapis.com/v0/b/myitarra-95fb4.appspot.com/o/media%2FItarra%20Co-Pilot.pdf?alt=media&token=91141a8e-baff-4fd4-9f61-5884895c096d" target="_blank">
                            Co-Pilot Construction Dumper Project
                            </a>
                        </li>
                        <li>
                            <a href="https://firebasestorage.googleapis.com/v0/b/myitarra-95fb4.appspot.com/o/media%2FItarra%20Midi.pdf?alt=media&token=1bdc487c-8dd3-4afe-838f-36c97f3f1208" target="_blank">
                            Midi Tractor Project
                            </a>
                        </li>
                        </ul>
                    </td>
                    </tr>

                    <!-- Video Section -->
                    <tr>
                      <td style="padding:30px; text-align:center; background:#ffffff; color:#333333;">
                        <h3 style="margin:0 0 15px 0; color:#111111;">🎥 Watch Our 5G Collaboration</h3>
                        <p>Three Ireland, TUS, and iTARRA working together on <b>5G Standalone</b>.</p>
                        <a href="https://youtu.be/jJtulwHh93Q" target="_blank"
                          style="display:inline-block; background:#30BDFF; color:#ffffff; text-decoration:none; 
                                 padding:14px 28px; border-radius:6px; font-size:16px; font-weight:bold;">
                          ▶ Watch on YouTube
                        </a>
                      </td>
                    </tr>

                    <!-- Footer -->
                    <tr>
                      <td style="padding:20px; background:#111111; color:#aaaaaa; text-align:center; font-size:13px;">
                        <p style="margin:0;">iTARRA | The Future is Different</p>
                        <p style="margin:5px 0 0;">
                          <a href="https://www.itarra.com" style="color:#aaaaaa; text-decoration:none;">www.itarra.com</a>
                        </p>
                        <p style="margin:10px 0 0;">© 2025 iTARRA. All rights reserved.</p>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </body>
          </html>
        `,
        bodyText: `Dear ${userData.name || "Friend"},\n\nThank you for your interest in iTARRA. We’re working with ATU Sligo, TUS Athlone, Three Ireland, and Google Cloud to deliver innovation at scale.`,
      });
    } catch (err) {
      console.error("❌ Email error:", err);
      throw err;
    }
  }

  return (
    <div
      style={{
        textAlign: "center",
        height: "100vh",
        width: "100vw",
        backgroundColor: "#000",
        backgroundImage: `url(${BackgroundImg})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        paddingTop: "40px",
        boxSizing: "border-box",
        color: "#fff",
      }}
    >
      <h2>✅ Thank you!</h2>
      <p>{message}</p>

      {emailStatus === "sending" && <p>📧 Sending you an email now...</p>}
      {emailStatus === "sent" && <p>✅ Email has been sent to {userData?.email}</p>}
      {emailStatus === "error" && <p>❌ Failed to send email. Please try again later.</p>}

      {/* Visit Us Button */}
      <Button
        variant="outlined"
        sx={{
          mt: 2,
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          backgroundColor: "transparent",
          "&:hover": {
            borderColor: theme.palette.primary.dark,
            color: theme.palette.primary.dark,
            backgroundColor: "rgba(255,255,255,0.08)",
          },
        }}
        onClick={() => window.open("https://www.itarra.com", "_blank")}
      >
        Visit us at www.itarra.com
      </Button>
    </div>
  );
};

export default DemoComplete;
